import React, { useContext, useEffect, useState } from 'react'
import { Table, Button, Dropdown, Typography, Affix, App, Popconfirm, Upload, Space, TreeSelect } from 'antd'
import { isMobileOnly, isDesktop } from "react-device-detect"
import _ from "lodash"
import {FaUpload, FaEllipsisH, FaInbox} from "react-icons/fa"
import dayjs from "dayjs";
import { API, Enum } from "../../../resources/constants"
import language from "../../../resources/languages/fa_IR"

// Contexts
import GlobalContext from "../../../contexts/global";
import AxiosContext from "../../../contexts/axios"

// Interfaces
import { AxiosError, AxiosResponsePaginate } from "../../../interfaces/axios"
import { AntTableParams } from "../../../interfaces/ant"
import type { DataNode } from 'antd/es/tree';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import type { FilterValue, SorterResult } from 'antd/es/table/interface'
import type { UploadFile } from 'antd/es/upload/interface'

const { Pages: { FileManagement: { File: FileLanguage } }, Share, Message: MessageLanguage } = language
const { FILE_MANAGEMENT_DIRECTORY_ALL, FILE_MANAGEMENT_FILE_PAGINATE, FILE_MANAGEMENT_FILE_REMOVE, FILE_MANAGEMENT_FILE_UPLOAD, FILE_MANAGEMENT_FILE_DOWNLOAD } = API
const { Message } = Enum

interface FileType {
    row: number,
    id: string,
    key: string,
    originalName: string,
    mimeType: string,
    size: number,
    createdAt: Date,
}

export default function Index() {

    const [, forceUpdate] = useState({})
    const [files, setFiles] = useState<FileType[]>([])
    const [sort, setSort] = useState<string|false|null|undefined>('createdAt,desc')
    const [loadingRecords, setLoadingRecords] = useState<boolean>(false);
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        total: undefined,
        current: 1,
        pageSize: 10,
        showTotal: isMobileOnly ? undefined : (total: number): string => `${Share.paginationShowTotal} (${total})`,
        showSizeChanger: !isMobileOnly,
        showQuickJumper: !isMobileOnly,
    });
    const [fileList, setFileList] = useState<UploadFile[]>([])
    const [query, setQuery] = useState<string>("")
    const [directory, setDirectory] = useState<string>()
    const [loadingDirectories, setLoadingDirectories] = useState<boolean>(true)
    const [directories, setDirectories] = useState<DataNode[]>([])
    const { accessToken, baseURL } = useContext(GlobalContext)
    const { axios } = useContext(AxiosContext)
    const { message, modal } = App.useApp();

    const columns: ColumnsType<FileType> = [
        {
            title: Share.row,
            dataIndex: "row",
            key: "row",
        },
        {
            title: FileLanguage.originalName,
            dataIndex: 'originalName',
            key: 'originalName',
            sorter: true,
        },
        {
            title: FileLanguage.directory,
            dataIndex: ['directory', 'title'],
            key: 'directory',
            sorter: true,
        },
        {
            title: FileLanguage.mimeType,
            dataIndex: 'mimeType',
            key: 'mimeType',
            sorter: true,
        },
        {
            title: FileLanguage.size,
            dataIndex: 'size',
            key: 'size',
            sorter: true,
            render: (text, record) => text && (text/1024/1024).toFixed(2)
        },
        {
            title: FileLanguage.createdAt,
            dataIndex: 'createdAt',
            key: 'createdAt',
            sorter: true,
            render: (text, record) => text && dayjs(text).tz(process.env.REACT_APP_TZ).format('HH:mm YYYY/MM/DD')
        },
        {
            title: Share.operation,
            dataIndex: "operation",
            key: "operation",
            render: (text, record) =>  <Dropdown menu={{ items: [
                    {
                        key: 'copy',
                        label: <Typography.Link onClick={() => {
                            navigator?.clipboard?.writeText(`${baseURL}${FILE_MANAGEMENT_FILE_DOWNLOAD}${record.id}`)
                            message.success(MessageLanguage[Message.COPY_CLIPBOARD]).then(response => response)
                        }}>{Share.copy}</Typography.Link>,
                    },
                    {
                        key: 'download',
                        label: <Typography.Link href={`${baseURL}${FILE_MANAGEMENT_FILE_DOWNLOAD}${record.id}`}>{Share.download}</Typography.Link>,
                    },
                    {
                        key: 'delete',
                        label: <Typography.Link onClick={() => modal.confirm({
                            title: Share.deleteConfirmTitle,
                            okText: Share.deleteConfirmOk,
                            okType: 'danger',
                            cancelText: Share.deleteConfirmCancel,
                            onOk() {
                                setLoadingRecords(true)
                                axios.delete(FILE_MANAGEMENT_FILE_REMOVE + record.id).then((response: {}) => {
                                    const { total = 1, pageSize = 1, current = 1 } = pagination
                                    const params = {
                                        page: total % pageSize !== 1 ? current : current - 1,
                                        limit: pageSize,
                                        sort,
                                    };
                                    setLoadingRecords(true)
                                    getFiles(params)
                                    message.success(MessageLanguage[Message.DELETE_FILE]).then(response => response)
                                }).catch((error: AxiosError) => {
                                    const { response: { data } } = error;
                                    // @ts-ignore
                                    message.error(MessageLanguage[data.message]).then(response => response )
                                    setLoadingRecords(false)
                                })
                            }
                        })}>{ Share.delete }</Typography.Link>,
                    },
                ] }} placement="bottomRight"><a><FaEllipsisH /></a></Dropdown>
        }
    ]

    const getFiles = async (params: AntTableParams) => axios.post(FILE_MANAGEMENT_FILE_PAGINATE, null, { params }).then((response: AxiosResponsePaginate) => {
        const { data: { docs: files, page: current, limit: pageSize, totalDocs: total } } = response
        const { sort } = params
        let row = (current - 1) * pageSize;
        setFiles(files.map<FileType>((file: FileType) => ({ ...file, row: ++row, key: file.id })))
        setLoadingRecords(false)
        setSort(sort)
        setPagination({
            ...pagination,
            current,
            pageSize,
            total,
        })
    }).catch((error: AxiosError) => {
        const { response: { data } } = error;
        // @ts-ignore
        message.error(MessageLanguage[data.message]).then(response => response )
    })

    useEffect(() => {
        // To disable submit button at the beginning.
        forceUpdate({});
        initialAPI().then(response => response);
    }, []);

    useEffect(() => {
        setQuery(directory ? `&directory=${directory}` : "")
    }, [directory]);

    const initialAPI = async () => {
        const { current, pageSize } = pagination
        setLoadingRecords(true)
        await getFiles({ page: current, limit: pageSize, sort })
        setLoadingDirectories(true)
        await getDirectories();
    }

    const getDirectories = async () => axios.post(FILE_MANAGEMENT_DIRECTORY_ALL).then((response: { data: [] }) => {
        const { data: directories } = response
        setDirectories(filter(directories))
        setLoadingDirectories(false)
    }).catch((error: AxiosError) => {
        const { response: { data } } = error;
        // @ts-ignore
        message.error(MessageLanguage[data.message]).then(response => response )
    })

    const filter = (directories: {
        id: string,
        key: string,
        value: string,
        title: string,
        children: [],
    }[]) => {
        for (let i = 0; i < directories.length; ++i) {
            directories[i]['key'] = directories[i]['id']
            directories[i]['value'] = directories[i]['id']
            directories[i]['title'] = directories[i]['title']
            if (!_.isEmpty(directories[i]['children'])) {
                filter(directories[i]['children'])
            }
        }
        return directories
    }

    const handleTableChange = (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue|null>,
        sorter: SorterResult<any>|SorterResult<any>[]
    ): void => {
        const { current, pageSize } = pagination
        const sort = !_.isArray(sorter) && sorter.order && `${sorter.field},${sorter.order && sorter.order.slice(0, -3)}`
        setLoadingRecords(true)
        getFiles({ page: current, limit: pageSize, sort });
    };

    const progress: any = () => ({
        strokeColor: {
            '0%': '#108ee9',
            '100%': '#87d068',
        },
        strokeWidth: 3,
        format: (percent: number) => percent && `${parseFloat(percent.toFixed())}%`,
    })

    return (
        <>
            <Table
                columns={columns}
                dataSource={files}
                pagination={pagination}
                loading={loadingRecords}
                onChange={handleTableChange}
                scroll={{ x: true }}
            />
            <Affix offsetBottom={30}>
                <Space.Compact block style={{marginTop:'10px'}}>
                <Popconfirm
                    overlayClassName={'popconfirm-upload'}
                    icon={null}
                    title={null}
                    description={
                        <Space direction="vertical" size={20} style={{width:'100%'}}>
                            <TreeSelect
                              style={{width:'100%'}}
                              loading={loadingDirectories}
                              showSearch
                              allowClear
                              treeDefaultExpandAll
                              treeNodeFilterProp="title"
                              placeholder={FileLanguage.directory}
                              treeData={directories}
                              onChange={(directory: string) => setDirectory(directory)}
                            />
                            <Upload.Dragger
                              action={`${baseURL}${FILE_MANAGEMENT_FILE_UPLOAD}?token=${accessToken}${query}`}
                              multiple={false}
                              name='file'
                              fileList={fileList}
                              onChange={(info: { file: UploadFile, fileList: UploadFile[] }) => {
                                  const { file, fileList } = info
                                  const { response, status } = file
                                  const { pageSize } = pagination
                                  setFileList(fileList)
                                  if (status === 'done') {
                                      setFileList([])
                                      setLoadingRecords(true)
                                      getFiles({ page: 1, limit: pageSize, sort })
                                      message.success(MessageLanguage[Message.UPLOAD_SUCCESS]).then(response => response)
                                  } else if (status === 'error') {
                                      setFileList([])
                                      // @ts-ignore
                                      message.error(MessageLanguage[response.message ? response.message : Message.UPLOAD_ERROR]).then(response => response)
                                  }
                              }}
                              progress={progress}
                            >
                                <p className="ant-upload-drag-icon"><FaInbox size={50} /></p>
                                <p className="ant-upload-text">{ Share.attachment }</p>
                                <p className="ant-upload-hint">{ Share.attachmentDescription }</p>
                            </Upload.Dragger>
                        </Space>
                    }
                >
                    <Button key='upload' style={{height:'40px'}} icon={<FaUpload />}>{ Share.upload }</Button>
                </Popconfirm>
                </Space.Compact>
            </Affix>
        </>
    )
}
